import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
// (services/msg91Direct.js retained but UNUSED — browsers cannot call MSG91
//  directly due to CORS. OTP send/verify go through the Lambda. The helper
//  module stays in case we later add a CORS-enabled proxy.)

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

const BASE = (process.env.REACT_APP_API_URL ||
  'https://ds5hf43t45.execute-api.ap-southeast-2.amazonaws.com/prod').replace(/\/$/, '');

async function apiPost(path, body) {
  try {
    const res = await fetch(`${BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { error: data.message || `Error ${res.status}`, data: null };
    return { data, error: null };
  } catch (e) {
    return { error: e.message || 'Network error', data: null };
  }
}

// Fetch real user profile from Users DynamoDB table by phone
async function fetchUserProfile(phone10) {
  try {
    const r = await fetch(`${BASE}/users/me?phone=${encodeURIComponent(phone10)}`);
    if (!r.ok) return null;
    const d = await r.json();
    return d; // { patientId, name, phone, email }
  } catch { return null; }
}

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast,   setToast]   = useState(null);

  // Restore session from localStorage on load
  useEffect(() => {
    try {
      const stored = localStorage.getItem('hcen_user');
      const token  = localStorage.getItem('hcen_token');
      if (stored && token) {
        const u = JSON.parse(stored);

        // Self-heal: ensure cached patientId is in canonical 91XXXXXXXXXX form.
        // Older sessions cached `phone_91XXX@hcen.app` or `+91XXX`. The Lambda
        // still matches them via multi-variant scan, but normalizing here keeps
        // every subsequent request consistent with the Flutter app.
        const phone10 = (u.phone || '').replace(/\D/g, '').slice(-10);
        if (phone10 && phone10.length === 10) {
          const canonical = `91${phone10}`;
          if (u.patientId !== canonical) {
            u.patientId = canonical;
            localStorage.setItem('hcen_user', JSON.stringify(u));
          }
        }
        setUser(u);

        // Silently refresh name from backend in background.
        if (phone10) {
          fetchUserProfile(phone10).then(profile => {
            if (profile?.name && profile.name !== u.name) {
              const updated = { ...u, name: profile.name };
              localStorage.setItem('hcen_user', JSON.stringify(updated));
              setUser(updated);
            }
          });
        }
      }
    } catch (_) {}
    setLoading(false);
  }, []);

  // ── Send OTP via WhatsApp (Lambda → MSG91, same template as Flutter app) ──
  // Lambda /auth/otp/send fires the same `hcenotp` MSG91 WhatsApp template the
  // Flutter app uses. We send the request server-side instead of from the
  // browser because browsers block direct cross-origin POSTs to api.msg91.com.
  const sendOtp = async (phone) => {
    const digits = phone.replace(/\D/g, '').slice(-10);
    const r = await apiPost('/auth/otp/send', { phone: digits });
    if (!r.error && r.data && r.data.delivered === false) {
      return { error: r.data.deliveryError || 'Could not deliver OTP. Please try again.', data: r.data };
    }
    return r;
  };

  // ── Verify OTP via Lambda /auth/otp/verify ───────────────────
  // Lambda reads `body.otp`. The OTP was generated and stored server-side by
  // /auth/otp/send (in PhoneOtps DynamoDB table) so this is a single
  // authoritative check — no client-side trust required.
  const verifyOtp = async (phone, code) => {
    const digits = phone.replace(/\D/g, '').slice(-10);
    const r = await apiPost('/auth/otp/verify', { phone: digits, otp: code, code });

    if (!r.error && r.data) {
      // Try to get real name from Users table (where Flutter app stores it)
      const profile = await fetchUserProfile(digits);

      // Normalize patientId → always 91XXXXXXXXXX (strips phone_@hcen.app if returned)
      let rawId = profile?.patientId || r.data.patientId || `91${digits}`;
      if (rawId.startsWith('phone_') && rawId.includes('@hcen.app')) {
        rawId = rawId.replace('phone_', '').replace(/@hcen\.app$/, '');
      }
      rawId = rawId.replace(/^\+/, '');                        // strip leading +
      const pid10 = rawId.replace(/\D/g, '').slice(-10);
      const canonicalId = pid10.length === 10 ? `91${pid10}` : rawId;

      const u = {
        patientId:   canonicalId,                              // 91XXXXXXXXXX
        name:        profile?.name || r.data.name || '',
        phone:       `+91${digits}`,
        phoneDigits: digits,
      };
      localStorage.setItem('hcen_user',  JSON.stringify(u));
      localStorage.setItem('hcen_token', r.data.token || r.data.idToken || 'session');
      setUser(u);
    }
    return r;
  };

  const logout = () => {
    localStorage.removeItem('hcen_user');
    localStorage.removeItem('hcen_token');
    setUser(null);
  };

  // ── Update user locally (e.g. after profile edit) ─────────────
  const updateUser = (updates) => {
    setUser(prev => {
      if (!prev) return prev;
      const updated = { ...prev, ...updates };
      localStorage.setItem('hcen_user', JSON.stringify(updated));
      return updated;
    });
  };

  // ── Toast ─────────────────────────────────────────────────────
  const showToast = useCallback((msg, type = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, sendOtp, verifyOtp, logout, updateUser, showToast }}>
      {children}
      {toast && (
        <div className="toast-container">
          <div className={`toast ${toast.type}`}>{toast.msg}</div>
        </div>
      )}
    </AuthCtx.Provider>
  );
}
